import { useState } from 'react';
import { api } from '../lib/api';
import { LoadingSpinner } from './ui/LoadingSpinner';

interface TermsGateProps {
  onAccepted: () => void;
  onSignOut: () => void;
}

/**
 * Full-screen blocking gate shown when the user hasn't accepted the current
 * T&C version. The user must review and accept before accessing the app.
 */
export function TermsGate({ onAccepted, onSignOut }: TermsGateProps) {
  const [checked, setChecked] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  async function handleAccept() {
    if (!checked) return;
    setSubmitting(true);
    setError('');
    try {
      await api.post('/auth/accept-terms', {});
      onAccepted();
    } catch {
      setError('Failed to save your acceptance. Please try again.');
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <div className="w-full max-w-md bg-white rounded-xl shadow-lg p-6 sm:p-8 space-y-6">
        <div className="text-center space-y-2">
          <h1 className="text-xl font-bold text-gray-900">Updated Terms & Conditions</h1>
          <p className="text-sm text-gray-600 leading-relaxed">
            We've updated our Terms and Conditions. Please review and accept to continue using IOMI.
          </p>
        </div>

        <a
          href="/terms"
          target="_blank"
          rel="noopener noreferrer"
          className="block w-full text-center text-sm font-semibold text-plum-600 hover:text-plum-700 underline"
        >
          Read Terms & Conditions
        </a>

        <label className="flex items-start gap-3 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={checked}
            onChange={e => setChecked(e.target.checked)}
            className="mt-0.5 h-5 w-5 rounded border-gray-300 text-plum-600 focus:ring-plum-500 shrink-0"
          />
          <span className="text-sm text-gray-700 leading-snug">
            I agree to the updated Terms & Conditions
          </span>
        </label>

        {error && (
          <p className="text-sm text-red-600">{error}</p>
        )}

        <button
          onClick={handleAccept}
          disabled={!checked || submitting}
          className="w-full py-3 rounded-lg text-sm font-semibold text-white bg-plum hover:bg-rose disabled:bg-stone disabled:text-slate disabled:cursor-not-allowed transition-colors"
        >
          {submitting ? <LoadingSpinner size="sm" /> : 'Continue'}
        </button>

        <button
          type="button"
          onClick={onSignOut}
          className="w-full text-center text-sm text-gray-500 hover:text-gray-700 transition-colors"
        >
          Return to Login
        </button>
      </div>
    </div>
  );
}
